// Single source of truth for "is the operator currently accepting real
// payments". All UI that branches on payment-availability — Pricing CTAs,
// Account "Manage subscription" button, UsageChip Add-credits button,
// future paywall modals — should consume this hook so we can change the
// gate (e.g. add per-region rules) in one place.

import { useQuery } from '@tanstack/react-query';
import { api } from './api';
import type { PackagesResponse } from '../types/api';

const PACKAGES_QUERY_KEY = ['billing', 'packages'] as const;

// Static fallback mirroring backend/pricing.py TIERS + TOPUP_PACKS. The
// Pricing page is a top-of-funnel conversion surface — it must NEVER render
// empty (it used to collapse to just the Free card whenever /billing/packages
// was slow or errored, because the paid cards come from the API). With this
// fallback the full ladder always shows; the live query overrides it the moment
// it lands. billing_live stays false so CTAs stay fail-closed.
// 2026-06-26 repricing: TWO tiers — Free + Pro $20/mo ($16.70/mo billed yearly);
// the old $9 Pro / $19 Max split is retired (max → pro). Keep in sync with
// backend/pricing.py.
// Mirrors backend/pricing.py UPLOAD_LIMITS_MB — used ONLY until the live query
// lands. The server is the authority (it raises the 413); this only drives the
// picker's label and pre-check, so a stale value here costs a wrong label,
// never a wrong outcome. Keep in sync with pricing.py.
const FALLBACK_UPLOAD_LIMITS_MB: Record<string, number> = {
  project_source: 500, asset: 500, caption: 100, transcribe: 200,
  align: 200, voice_sample: 50,
};

const FALLBACK_PACKAGES: PackagesResponse = {
  currency: 'USD',
  billing_live: false,
  // Same object as the hook's fallback — one table, not two.
  upload_limits_mb: FALLBACK_UPLOAD_LIMITS_MB,
  tiers: [
    { plan: 'free', name: 'Free', monthly_usd: 0, yearly_usd: 0, minutes_per_month: 10, minutes_per_day: 5, max_video_minutes: 3, concurrency: 1, storage_gb: 2, ai_seconds_per_month: 0, watermark: true, voice_clone: false },
    { plan: 'pro', name: 'Pro', monthly_usd: 20, yearly_usd: 16.7, minutes_per_month: 300, minutes_per_day: 100, max_video_minutes: 45, concurrency: 5, storage_gb: 100, ai_seconds_per_month: 60, watermark: false, voice_clone: true },
  ],
  subscriptions: [
    { id: 'pro', name: 'Pro', monthly_usd: 20, yearly_usd: 16.7, yearly_total: 200, monthly_credits: 3000, popular: true, bonus_pct: 0 },
  ],
  topups: [
    { id: 'ai_60', usd: 9, credits: 600, label: 'AI 视频 60 秒', ai_seconds: 60 },
    { id: 'ai_300', usd: 39, credits: 3000, label: 'AI 视频 300 秒', ai_seconds: 300 },
  ],
};

/** Full billing catalog (tiers + topups + flags). Cached by TanStack
 *  Query so multiple components share the same fetch. `data` always
 *  resolves to at least FALLBACK_PACKAGES so the Pricing ladder never
 *  renders empty on a slow/failed fetch. */
export function usePackages() {
  const q = useQuery({
    queryKey: PACKAGES_QUERY_KEY,
    queryFn: () => api<PackagesResponse>('/api/v2/billing/packages'),
    staleTime: 60_000,
  });
  return { ...q, data: q.data ?? FALLBACK_PACKAGES };
}

/** Upload ceiling in MB for one key of pricing.UPLOAD_LIMITS_MB.
 *
 *  ★ Read it, don't retype it. Every one of these numbers used to be written
 *    twice — the caption cap lived in routes/caption.py AND as a literal
 *    `100 * 1024 * 1024` in Caption.tsx, so raising one silently left the
 *    other behind. Now the server ships the table on /billing/packages. */
export function useUploadLimitMb(key: string): number {
  const q = usePackages();
  return q.data?.upload_limits_mb?.[key] ?? FALLBACK_UPLOAD_LIMITS_MB[key] ?? 100;
}

/** Boolean form of the BILLING_LIVE flag. Defaults to `false` while the
 *  query is loading or has errored — fail closed so we never show
 *  enabled CTAs that would fail with 503 on click. */
export function useBillingLive(): boolean {
  const q = usePackages();
  return !!q.data?.billing_live;
}
